// The stage is the film's own coordinate system (3840 x 2160 source pixels), scaled to cover the
// window like `object-fit: cover`. Everything placed on the film (the CRT layer, key hotspots,
// the close-up loop) is positioned in source pixels inside #stage, so it stays glued to the picture
// at any window size.

import type { Rect } from "./media";

export const SRC_W = 3840;
export const SRC_H = 2160;

export interface View {
  /** The part of the source that must fill the window (cover). Full frame by default. */
  rect: Rect;
}

export const FULL: Rect = { x: 0, y: 0, w: SRC_W, h: SRC_H };

export class Stage {
  private view: Rect = FULL;

  constructor(private el: HTMLElement) {
    el.style.width = `${SRC_W}px`;
    el.style.height = `${SRC_H}px`;
    window.addEventListener("resize", () => this.apply(false));
    this.apply(false);
  }

  /** Scale and offset that make `rect` cover the window, centred. */
  transformFor(rect: Rect): { s: number; tx: number; ty: number } {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const s = Math.max(W / rect.w, H / rect.h);
    const tx = W / 2 - (rect.x + rect.w / 2) * s;
    const ty = H / 2 - (rect.y + rect.h / 2) * s;
    return { s, tx, ty };
  }

  /** Move the view; with `animate`, the CSS transition on #stage does the glide. */
  show(rect: Rect, animate = true): void {
    this.view = rect;
    this.apply(animate);
  }

  private apply(animate: boolean): void {
    const { s, tx, ty } = this.transformFor(this.view);
    if (animate) this.el.classList.add("gliding");
    this.el.style.transform = `translate(${tx}px, ${ty}px) scale(${s})`;
  }

  /** Current scale: CSS pixels per source pixel. */
  scale(): number {
    return this.transformFor(this.view).s;
  }

  /**
   * Glide to `rect` (the CSS transition on #stage.gliding) and resolve when it has arrived.
   * Without `animate` the view jumps. Bounded, in case the transition event never comes.
   */
  glide(rect: Rect, animate = true): Promise<void> {
    if (!animate) {
      this.show(rect, false);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.el.removeEventListener("transitionend", onEnd);
        this.el.classList.remove("gliding");
        resolve();
      };
      const onEnd = (ev: TransitionEvent) => {
        if (ev.target === this.el && ev.propertyName === "transform") finish();
      };
      this.el.addEventListener("transitionend", onEnd);
      window.setTimeout(finish, 3200);
      // two frames so the start transform is committed before the class change
      requestAnimationFrame(() => requestAnimationFrame(() => this.show(rect, true)));
    });
  }

  /** Window position of a source point (for tests and for hit-testing). */
  toWindow(x: number, y: number): { x: number; y: number } {
    const { s, tx, ty } = this.transformFor(this.view);
    return { x: tx + x * s, y: ty + y * s };
  }
}
